import { useDropzone } from "react-dropzone";
import { styled } from "@mui/material/styles";

import React from "react";
import MuiButton from "../MuiButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { CloudUpload, Delete } from "../SvgIcons";
import { Alert } from "@mui/material";

import { useTranslation } from "react-i18next";
import { IFormField } from "../../../models/app";

interface Props extends IFormField {
  errors: any;
  control: any;
  required?: boolean;
  defaultValue?: any;
  watch?: any;
  onDeleteFile?: any;
  setValue?: any;
}

const DropzoneContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: "2px",
  borderRadius: "2px",
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
});

function RHDropzoneSingle({
  label,
  name,
  onChange,
  placeholder,
  disabled,
  fileType,
  autoFocus,
  control,
  errors,
  required,
  defaultValue,
  onDeleteFile,
  defaultImageUrl,
  watch,
  hidden,
  setValue,
}: Props) {
  const [file, setFile] = React.useState<any>(defaultValue || null);

  const [defaultImageUrlState, setDefaultImageUrlState] = React.useState<
    string | any
  >(defaultImageUrl);
  const [errMessage, setErrMessage] = React.useState<string>("");
  const limitFileSize = 2.5 * 1024 * 1024; // 2.5 MB
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles: any) => {
      const uploadedFile = acceptedFiles[0];
      if (uploadedFile.size > limitFileSize) {
        setErrMessage("File size should be less than or equal to 2.5 MB");
        setTimeout(() => setErrMessage(""), 5000);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(uploadedFile);
      reader.onload = (url) => {
        const fileUrl = url?.target?.result;
        Object.assign(uploadedFile, { fileUrl });
        setFile(uploadedFile); // Replace the previous file
        setValue && setValue(name, uploadedFile);
        onChange && onChange(uploadedFile);
      };
    },
    accept: {
      "image/*": [".png", ".webp", ".jpeg", ".jpg"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"]
    },
    
  });

  const handleDelete = () => {
    setFile(null);
    setValue && setValue(name, null);
    onChange && onChange(null);
  };

  const { t } = useTranslation();
  const handleDeleteCloudImage = () => {
    if (disabled) return;
    onDeleteFile && onDeleteFile();
    setDefaultImageUrlState(null);
    onChange && onChange(null);
  };

  return (
    <Stack spacing={1} width={"100%"}>
      {label && (
        <Typography variant="body2" fontWeight={600} textAlign={"start"} mr={3}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          maxHeight: 350,
          border: "1px dashed #ccc",
          borderRadius: 1,
          p: 2,
          bgcolor: "#FFFFFF",
          overflowY: "auto",
        }}
      >
        {!file && !defaultImageUrlState && (
          <DropzoneContainer {...getRootProps()}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <Typography variant="h5" component="h2">
                Drop the file here ...
              </Typography>
            ) : (
              <>
                <MuiButton>
                  <CloudUpload sx={{ fontSize: 48 }} />
                </MuiButton>
                <Typography variant="h5" component="h2">
                  {t("T_drop_file")}
                </Typography>
                <Typography variant="caption" component="h6">
                  {t("T_file_size")}
                </Typography>
              </>
            )}
          </DropzoneContainer>
        )}
        {file && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            mb={2}
            width={"100%"}
            maxHeight={300}
            position="relative"
            sx={{
              "&:hover": { opacity: 0.5 },
              cursor: "pointer",
            }}
            onClick={handleDelete}
          >
            {fileType === 'other'? <div>file</div>: <Box
              component={"img"}
              src={file?.fileUrl || file}
              alt={file?.name || "image"}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: 1,
              }}
            /> }
            
            <IconButton
              aria-label="delete"
              size="small"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                color: "red",
                visibility: "visible",
              }}
            >
              <Delete />
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {file.name}
            </Typography>
          </Box>
        )}
        {defaultImageUrlState && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            mb={2}
            width={"100%"}
            maxHeight={300}
            maxWidth={400}
            position="relative"
            sx={{
              "&:hover": { opacity: !disabled ? 0.5 : 1 },
              cursor: "pointer",
            }}
            onClick={handleDeleteCloudImage}
          >
            <Box
              component={"img"}
              src={defaultImageUrlState}
              alt={defaultImageUrlState}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
            {!disabled && (
              <IconButton
                aria-label="delete"
                size="small"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  color: "red",
                  visibility: "visible",
                }}
              >
                <Delete />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
      <Alert severity="error" sx={{ display: errMessage ? "flex" : "none" }}>
        {errMessage}
      </Alert>
    </Stack>
  );
}

export default RHDropzoneSingle;
